import { translateComponent } from 'i18n';
import { useState } from 'react';
import {
    AutocompletAble,
    formatValueForAutocomplete,
} from 'ui/DropdownItems/types';
import {
    useMutation,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import { useDeepCompareEffect } from 'helpers/reactUseHelper';
import {
    InitializeInscriptionPayload,
    InitInscriptionsLogs,
    InscriptionsModalOptions,
    SoftwareConfig,
} from './type';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useInscriptionOptionQuery } from 'routes/BudgetModule/helpers/hooks/useCustomQuery';

/**
 * Custom hook to fetch inscription options for the inscriptions initialisation.
 */
export const useGetInscriptionOptionsQuery = (): UseQueryResult<
    InscriptionsModalOptions,
    HttpError
> => {
    const { inscriptionOptionsKeys } = createBudgetQueryKeys();
    return useInscriptionOptionQuery<InscriptionsModalOptions, HttpError>(
        inscriptionOptionsKeys(),
        async () => {
            const response = await RQAPICallWithData<InscriptionsModalOptions>({
                endpoint: 'budget/admin/create_inscription_options',
                method: 'GET',
            });
            return response.content.data;
        },
    );
};

/**
 * Handle all the value/setter/available options for each inscriptions initialisation modal form field
 */
export const useGetInitInscriptionsFields = (
    options?: InscriptionsModalOptions,
) => {
    const { t } = useTranslation();
    const [availableYears, setAvailableYears] = useState<
        AutocompletAble<number>[]
    >([]);
    const [availableSteps, setAvailableSteps] = useState<
        AutocompletAble<string>[]
    >([]);
    const [availableSoftwaresConfigs, setAvailableSoftwaresConfigs] = useState<
        AutocompletAble<SoftwareConfig>[]
    >([]);
    const [
        availableExistingInscriptionsActions,
        setAvailableExistingInscriptionsActions,
    ] = useState<AutocompletAble<string>[]>([]);
    const [
        availableResetAmountAndCommentActions,
        setAvailableResetAmountAndCommentActions,
    ] = useState<AutocompletAble<string>[]>([]);
    const [availableCommentColumns, setAvailableCommentColumns] = useState<
        AutocompletAble<string>[]
    >([]);
    const [availableInscriptionSources, setAvailableInscriptionSources] =
        useState<AutocompletAble<string>[]>([]);
    const [availablePhases, setAvailablePhases] = useState<
        AutocompletAble<string>[]
    >([]);
    const [availableKeepProjectActions, setAvailableKeepProjectActions] =
        useState<AutocompletAble<string>[]>([]);

    const [inscriptionSource, setInscriptionSource] =
        useState<AutocompletAble<string> | null>(null);
    const [phase, setPhase] = useState<AutocompletAble<string> | null>(null);
    const [keepProjectAction, setKeepProjectAction] =
        useState<AutocompletAble<string> | null>(null);
    const [year, setYear] = useState<AutocompletAble<number> | null>(null);
    const [inscriptionSteps, setInscriptionSteps] = useState<
        AutocompletAble<string>[] | null
    >(null);
    const [resourceSoftwareIds, setResourceSoftwareIds] = useState<
        AutocompletAble<string>[] | null
    >(null);
    const [selectedBudgetProposals, setSelectedBudgetProposals] = useState<
        AutocompletAble<BudgetProposalWithResourcesT>[] | null
    >(null);
    const [softwareConfig, setSoftwareConfig] =
        useState<AutocompletAble<SoftwareConfig> | null>(null);
    const [existingInscriptionsAction, setExistingInscriptionsAction] =
        useState<AutocompletAble<string> | null>(null);

    const [resetAmountAndCommentAction, setResetAmountAndCommentAction] =
        useState<AutocompletAble<string> | null>(null);
    const [commentColumn, setCommentColumn] =
        useState<AutocompletAble<string> | null>(null);

    const [confirmationIsValid, setConfirmationIsValid] =
        useState<boolean>(false);

    useDeepCompareEffect(() => {
        if (options === undefined) return;
        setAvailableYears(
            options.year
                .filter(Boolean)
                .map((yearOption: number) =>
                    formatValueForAutocomplete(
                        yearOption,
                        false,
                        yearOption.toString(),
                    ),
                ),
        );
        setAvailableSoftwaresConfigs(
            options.softwareConfig
                .filter(Boolean)
                .map((softwareOption) =>
                    formatValueForAutocomplete(
                        softwareOption,
                        false,
                        softwareOption.software,
                    ),
                ),
        );
        setAvailableSteps(
            options.step
                .filter(Boolean)
                .map((inscriptionStep) =>
                    formatValueForAutocomplete(
                        inscriptionStep,
                        false,
                        inscriptionStep,
                    ),
                ),
        );
        setAvailableExistingInscriptionsActions(
            options.keepInscription.map((action) =>
                formatValueForAutocomplete(
                    action,
                    false,
                    translateComponent(
                        `adminBudgetSpace.initInscriptions.keepInscriptions.${action}`,
                    ),
                ),
            ),
        );
        setAvailableResetAmountAndCommentActions(
            options.resetInscriptionOption.map((action) =>
                formatValueForAutocomplete(
                    action,
                    false,
                    translateComponent(
                        `adminBudgetSpace.initInscriptions.resetAmountAndCommentAction.${action}`,
                    ),
                ),
            ),
        );
        setAvailableCommentColumns(
            options.commentColumn.map((commentColumnOption) =>
                formatValueForAutocomplete(
                    commentColumnOption,
                    false,
                    translateComponent(
                        `adminBudgetSpace.initInscriptions.commentColumn.${commentColumnOption}`,
                    ),
                ),
            ),
        );
        setAvailableInscriptionSources(
            options.inscriptionSource.map((inscriptionSourceOption) =>
                formatValueForAutocomplete(
                    inscriptionSourceOption,
                    false,
                    translateComponent(
                        `adminBudgetSpace.initInscriptions.inscriptionSource.${inscriptionSourceOption}`,
                    ),
                ),
            ),
        );
        setAvailablePhases(
            options.phase.map((phaseOption) =>
                formatValueForAutocomplete(
                    phaseOption,
                    false,
                    t(`budgetProposal.${phaseOption}`),
                ),
            ),
        );
        setAvailableKeepProjectActions(
            options.keepProject.map((action) =>
                formatValueForAutocomplete(
                    action,
                    false,
                    translateComponent(
                        `adminBudgetSpace.initInscriptions.keepProject.${action}`,
                    ),
                ),
            ),
        );
    }, [options]);
    return {
        year,
        setYear,
        inscriptionSteps,
        setInscriptionSteps,
        selectedBudgetProposals,
        setSelectedBudgetProposals,
        resourceSoftwareIds,
        setResourceSoftwareIds,
        softwareConfig,
        setSoftwareConfig,
        existingInscriptionsAction,
        setExistingInscriptionsAction,
        resetAmountAndCommentAction,
        setResetAmountAndCommentAction,
        commentColumn,
        setCommentColumn,
        confirmationIsValid,
        setConfirmationIsValid,
        inscriptionSource,
        setInscriptionSource,
        phase,
        setPhase,
        keepProjectAction,
        setKeepProjectAction,
        availableYears,
        availableSoftwaresConfigs,
        availableSteps,
        availableExistingInscriptionsActions,
        availableResetAmountAndCommentActions,
        availableCommentColumns,
        availableInscriptionSources,
        availablePhases,
        availableKeepProjectActions,
    };
};

export const useInitInscriptions = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { initialisationKeys } = createBudgetQueryKeys();

    return useMutation({
        mutationFn: async ({
            software,
            inscriptionSource,
            year,
            budgetProposalIds,
            resources,
            keepInscription,
            resetInscriptionOption,
            step,
            phase,
            commentColumn,
            keepProject,
        }: InitializeInscriptionPayload) => {
            const response = await RQAPICallWithData<InitInscriptionsLogs>({
                endpoint: `budget/admin/create_inscriptions`,
                method: 'POST',
                body: {
                    software,
                    inscriptionSource,
                    year,
                    budgetProposalIds,
                    resources,
                    keepInscription,
                    resetInscriptionOption,
                    step,
                    phase,
                    commentColumn,
                    keepProject,
                },
            });
            return response.content.data.executionSummary;
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: initialisationKeys({ isAvailable: true }),
            });
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.initInscriptions.successSnackBar'),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};
